import { TFunction } from 'i18next';
import { Callable } from 'common/frontend/callable';

import {
	Category,
	PosTriggerType,
	OrderProduct,
	OrderProductWithUnits,
	ShopApi,
	ShopReadOnlyData,
	SolteqItemObject,
	SolteqReceiptObject,
} from 'common/types';
import { notUndefined } from 'common/utils/common';
import {
	getProductNameWithDuration,
	getOrderProductsWithUnitsFromOrderProductList,
} from 'common/utils/productUtils';
import { getItemsPayments } from '../atoms/paymentAmounts';
import { shopHasFeatures } from '../plans/features';
import { getCloudPosOrderNumber } from './cloudPos';
import { getTeksoOrderNumber } from './tekso';

type PosIntegrationType = null | 'OSKAR' | 'TEKSO' | 'CLOUD_POS';

interface HandleReceiptIntegrationAction {
	shopId: string;
	shopReadOnly: ShopReadOnlyData | undefined;
	locationId: string;
	customer: {
		firstName: string;
		lastName: string;
	};
	orderDate: string;
	receiptProducts: OrderProduct[];
	categories: Category[];
	barCode: string;
	paymentNumber: number;
	paymentRequestId: string;
}

export const handlePosIntegrationAction = (
	{
		shopId,
		shopReadOnly,
		locationId,
		customer,
		orderDate,
		receiptProducts,
		barCode,
		paymentNumber,
		paymentRequestId,
		categories,
	}: HandleReceiptIntegrationAction,
	t: TFunction,
) => {
	const posIntegrationType = getPosIntegrationType(shopReadOnly);
	const unpaidAmont = getItemsPayments(receiptProducts).charge.unpaid;
	switch (posIntegrationType) {
		case 'TEKSO': {
			if (unpaidAmont === 0) return;
			const rentleOrderId = paymentRequestId;
			const { customerId, orderBaseNumber, customMerchantId } = shopReadOnly!.integrations!.TEKSO!; // Must be defined if TEKSO value returned
			const orderNumber = getTeksoOrderNumber(orderBaseNumber, paymentNumber);
			const receiptItems = getItemsForSolteqReceipt(receiptProducts, categories, t);
			const clientId = locationId;
			const receiptObject: SolteqReceiptObject = {
				merchantId: customMerchantId ?? shopId,
				clientId,
				customer,
				barCode,
				rentleOrderId,
				orderDate,
				customerId,
				orderNumber,
				items: receiptItems,
			};
			Callable.integrations.solteq.sendPosReceipt({ receiptObject, integrationType: 'TEKSO' });
			break;
		}
		case 'CLOUD_POS': {
			if (unpaidAmont === 0) return;
			const rentleOrderId = paymentRequestId;
			const { customerId, orderBaseNumber, customMerchantId, legacyConnectorIntegration } =
				shopReadOnly?.integrations?.CLOUD_POS ?? {};
			const orderNumber = getCloudPosOrderNumber(orderBaseNumber, paymentNumber);
			const receiptItems = getItemsForSolteqReceipt(receiptProducts, categories, t);
			const clientId = locationId;
			const receiptObject: SolteqReceiptObject = {
				merchantId: customMerchantId ?? shopId,
				clientId,
				customer,
				barCode,
				rentleOrderId,
				orderDate,
				customerId: customerId ?? '000000000',
				orderNumber,
				items: receiptItems,
			};
			Callable.integrations.solteq.sendPosReceipt({
				receiptObject,
				integrationType: legacyConnectorIntegration ? 'CLOUD_POS' : 'CLOUD_POS_DIRECT',
			});
			break;
		}
		default:
			return;
	}
};

export const getPosIntegrationType = (readOnly?: ShopReadOnlyData): PosIntegrationType => {
	if (!readOnly) {
		return null;
	}
	if (
		shopHasFeatures('TEKSO_POS')(readOnly.features) &&
		readOnly.integrations?.TEKSO?.customerId != null &&
		readOnly.integrations?.TEKSO?.orderBaseNumber != null
	) {
		return 'TEKSO';
	}
	if (shopHasFeatures('OSKAR_POS')(readOnly.features)) {
		return 'OSKAR';
	}

	if (shopHasFeatures('CLOUD_POS')(readOnly.features)) {
		return 'CLOUD_POS';
	}
	return null;
};

export const getExternalPosTriggerTypes = (data: {
	shopInfo?: ShopApi;
	readOnly?: ShopReadOnlyData;
}): PosTriggerType[] => {
	const integrationType = getPosIntegrationType(data.readOnly);
	if (!integrationType) return [];
	if (integrationType === 'CLOUD_POS') return data.shopInfo?.plugins?.cloudPos?.triggerTypes ?? [];
	if (integrationType === 'TEKSO') return data.shopInfo?.plugins?.teksoPos?.triggerTypes ?? [];
	return [];
};

export const getItemsForSolteqReceipt = (
	products: OrderProduct[],
	categories: Category[],
	t: TFunction,
) => {
	const orderProductsWithUnits = getOrderProductsWithUnitsFromOrderProductList(products);
	return getSolteqItemsFromOrderProductsWithUnits(orderProductsWithUnits, categories, t);
};

export const getSolteqItemsFromOrderProductsWithUnits = (
	orderProductsWithUnits: OrderProductWithUnits[],
	stockCategories: Category[],
	t: TFunction,
): SolteqItemObject[] => {
	const getCategoriesForItem = (
		categoryIds: string[],
		categories: Category[],
	): { name: string; id: string }[] => {
		return categoryIds
			.map((id) => {
				const category = categories.find((c) => c.id === id);
				if (!category) return undefined;
				return {
					name: category.name.def,
					id: category.id,
				};
			})
			.filter(notUndefined);
	};
	return orderProductsWithUnits.map((product) => ({
		name: getProductNameWithDuration(product, 'fi', 'short', t),
		quantity: product.units || 1,
		amount: {
			value: getItemsPayments(product).charge.unpaid + Math.abs(product.pricing.totalDiscounts),
			currency: product.pricing.currency,
		},
		...(!!product.pricing.totalDiscounts && {
			discount: Math.abs(product.pricing.totalDiscounts) * -1,
		}),
		categories: getCategoriesForItem(product.categoryIds ?? [], stockCategories),
		taxPercentage: product.vatPercent,
		productId: product.id,
		stockProductId: product.productApiId,
		variantId: product.variant?.id ?? null,
	}));
};
